import type { GeocodingFeature } from "@maptiler/sdk";
import type {
  MapCoords,
  SearchHookDivision,
  SearchHookDivisionWithCode,
  SearchHookLocation,
} from "../../types";

export function makeDivision<T extends { text: string }>(
  prop: T | undefined,
): SearchHookDivision | undefined {
  if (!prop) {
    return undefined;
  }

  return {
    name: prop.text,
  };
}

export function makeDivisionWithCode<
  T extends { text: string },
  K extends keyof T,
>(
  prop: (T & { [key in K]: string }) | undefined,
  key: K,
): SearchHookDivisionWithCode | undefined {
  if (!prop) {
    return undefined;
  }

  return {
    name: prop.text,
    code: prop[key],
  };
}

function getFeatureContext(
  feature: GeocodingFeature,
): SearchHookLocation["context"] {
  if (!feature.context) {
    return {};
  }

  const result: SearchHookLocation["context"] = {};

  const country = feature.context.find((c) => c.id.startsWith("country."));
  if (country) {
    result.country = makeDivisionWithCode(country, "country_code");
  }

  const postcode = feature.context.find((c) => c.id.startsWith("postal_code."));
  if (postcode) {
    result.postcode = makeDivision(postcode);
  }

  const district = feature.context.find((c) =>
    c.id.startsWith("municipality."),
  );
  if (district) {
    result.district = makeDivision(district);
  }

  const region = feature.context.find((c) => c.id.startsWith("region."));
  if (region) {
    result.region = makeDivision(region);
  }

  return result;
}

function getCoords(feature: GeocodingFeature): MapCoords {
  if (feature.geometry.type === "Point") {
    return {
      lat: feature.geometry.coordinates[1],
      lng: feature.geometry.coordinates[0],
    };
  }

  // TODO: handle this better
  return {
    lat: 0,
    lng: 0,
  };
}

export function suggestedFeatureToLocation(
  feature: GeocodingFeature,
): SearchHookLocation {
  return {
    name: feature.text,
    formattedLocation: feature.place_name,
    address: feature.address ?? "",
    language: feature.language,

    context: getFeatureContext(feature),
    coords: getCoords(feature),
    bbox: feature.bbox.slice(0, 4) as [number, number, number, number],
  };
}

export function geocodingFeatureToLocation(
  feature: GeocodingFeature,
): SearchHookLocation {
  const [minLong, minLat, maxLon, maxLat] = (feature.bbox as any) ?? [];

  return {
    name: feature.text,
    formattedLocation: feature.place_name,
    address: feature.address ?? "",

    context: getFeatureContext(feature),
    coords: getCoords(feature),

    bbox:
      minLong && minLat && maxLon && maxLat
        ? [minLong, minLat, maxLon, maxLat]
        : undefined,
  };
}
